import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import type { FeedbackType } from "@/lib/types";
import { cn } from "@/lib/cn";

const TYPES: { value: FeedbackType; label: string; hint: string }[] = [
  { value: "GOOD_BEHAVIOR", label: "Good example", hint: "Reinforce this approach" },
  { value: "MISTAKE",       label: "Mistake",      hint: "A concrete error to correct" },
  { value: "BAD_BEHAVIOR",  label: "Bad habit",    hint: "A tone or pattern to avoid" },
  { value: "PATTERN",       label: "Pattern",      hint: "A recurring situation to handle consistently" },
];

/**
 * Capture an agent-training entry from the message an operator is reading.
 *
 * <p>The Training page is where feedback is authored deliberately; this is
 * where it is captured in the moment, without leaving the conversation. Both
 * write the same rows and feed the same retrieval into email generation.
 *
 * <p>The conversation toggle defaults on because a single message is rarely
 * self-explanatory — "too pushy" only means something next to what the prospect
 * had just said — and the backend attaches the preceding messages so the
 * operator does not have to paste context by hand.
 */
export default function EmailFeedbackDialog({
  emailId,
  strategyId,
  onClose,
}: {
  emailId: string;
  strategyId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [type, setType] = useState<FeedbackType>("MISTAKE");
  const [comment, setComment] = useState("");
  const [includeConversation, setIncludeConversation] = useState(true);

  const submit = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/emails/${emailId}/feedback`, {
        type,
        comment: comment.trim() || null,
        includeConversation,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feedback", strategyId] });
      onClose();
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Send to agent training"
    >
      <div className="w-full max-w-lg rounded-lg border border-[var(--border)] bg-[var(--bg-card)] p-5 shadow-lg">
        <h2 className="text-base font-semibold">Send to agent training</h2>
        <p className="mt-1 text-xs text-[var(--fg-muted)]">
          Records this message as a training example. The agent retrieves it as a
          behavioural guideline when composing future emails for this strategy.
        </p>

        <fieldset className="mt-4">
          <legend className="text-sm font-medium mb-2">How should the agent read this?</legend>
          <div className="grid grid-cols-2 gap-2">
            {TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setType(t.value)}
                className={cn(
                  "rounded-md border p-2 text-left text-sm",
                  type === t.value
                    ? "border-[var(--primary)] bg-zinc-50 font-medium"
                    : "border-[var(--border)] hover:bg-zinc-50",
                )}
              >
                <div>{t.label}</div>
                <div className="text-xs text-[var(--fg-muted)]">{t.hint}</div>
              </button>
            ))}
          </div>
        </fieldset>

        <label className="mt-4 block text-sm font-medium" htmlFor="feedback-comment">
          What should it do instead? <span className="font-normal text-[var(--fg-muted)]">(optional)</span>
        </label>
        <textarea
          id="feedback-comment"
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="e.g. Acknowledge the objection before proposing a next step."
          className="mt-1 w-full rounded-md border border-[var(--border)] p-2 text-sm"
        />

        <label className="mt-3 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeConversation}
            onChange={(e) => setIncludeConversation(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            Include the conversation up to this message
            <span className="block text-xs text-[var(--fg-muted)]">
              Attaches the preceding messages so the example carries the situation
              that produced it.
            </span>
          </span>
        </label>

        {submit.error ? (
          <div className="mt-3 text-sm text-[var(--danger)]">
            {submit.error instanceof ApiError
              ? `Could not save feedback (HTTP ${submit.error.status}).`
              : "Could not save feedback."}
          </div>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-zinc-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => submit.mutate()}
            disabled={submit.isPending}
            className="rounded-md bg-[var(--primary)] px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          >
            {submit.isPending ? "Saving…" : "Save feedback"}
          </button>
        </div>
      </div>
    </div>
  );
}
